import { type ReactNode, useState } from 'react';
import { type AnyChainflipAsset } from '@chainflip/utils/chainflip';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { MacScrollbar } from 'mac-scrollbar';
import { AnimatePresence, motion } from 'motion/react';
import { type Filters } from '@/shared/components/dropdown/filter/FilterDropdown';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import CloseIcon from '@/shared/icons/flip-ui-kit/small/Close';
import { defaultAnimationProps } from '@/shared/utils/consts';

export const AssetValueLabel = ({ assets }: { assets: AnyChainflipAsset[] }) => {
  if (assets.length === 1) {
    return <TokenWithChainByAsset asset={assets[0]} />;
  }
  return (
    <div className="flex items-center gap-x-1">
      <div className="flex items-center -space-x-1.5">
        {assets.slice(0, 3).map((asset) => (
          <TokenLogo key={asset} token={asset} size="small" />
        ))}
      </div>
      <div>{assets.length} assets</div>
    </div>
  );
};

const SelectedFilterDropdown = <T,>({
  filter,
  value,
  valueLabel,
  selectedFilter,
  displayLabel,
}: {
  filter: Filters<keyof T>[number]['type'];
  value: T[keyof T];
  valueLabel: <K extends keyof T>(type: Filters<K>[number]['type'], value: T[K]) => ReactNode;
  selectedFilter: (type: Filters<keyof T>[number]['type']) => ReactNode;
  displayLabel?: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={(v) => setIsOpen(v)} modal={false}>
      <DropdownMenu.Trigger asChild>
        <div className="cursor-pointer px-1.5 transition hover:bg-cf-gray-5 hover:text-cf-white">
          <span>{displayLabel ?? valueLabel(filter, value)}</span>
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-10 w-full min-w-[222px] rounded-md border border-cf-gray-4 bg-cf-gray-3-5 font-aeonikMedium text-13 shadow-grayPop2"
          sideOffset={8}
          align="start"
        >
          <MacScrollbar skin="dark" className="max-h-[384px] overflow-y-scroll">
            {selectedFilter(filter)}
          </MacScrollbar>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

const ActiveFilterLabel = <T,>({
  filter,
  value,
  resetFilter,
  isLabel,
  valueLabel,
  selectedFilter,
  isEmpty,
  hideReset,
}: {
  filter: Filters<keyof T>[number];
  value: T[keyof T];
  resetFilter: () => void;
  isLabel: <K extends keyof T>(type: K, value: T[K]) => string;
  valueLabel: <K extends keyof T>(type: K, value: T[K]) => ReactNode;
  selectedFilter: (type: Filters<keyof T>[number]['type']) => ReactNode;
  isEmpty: boolean;
  hideReset?: boolean;
}) => (
  <motion.div className="hidden font-aeonikMedium lg:block" {...defaultAnimationProps}>
    <div
      className={classNames(
        '[&>div>span]:flex [&>div>span]:h-[24px] [&>div>span]:items-center [&>div>span]:rounded-[3px] [&>div>span]:p-1',
        '[&>div]:flex [&>div]:h-full [&>div]:items-center [&>div]:border-r [&>div]:border-r-cf-gray-4',
        '[&>div:last-child]:border-r-0',
        'flex h-[32px] w-fit items-stretch overflow-hidden rounded-md border border-cf-gray-4 bg-cf-gray-3-5 text-12',
      )}
    >
      <div className="flex items-center gap-x-1 p-2">
        <div className="h-[16px] w-[16px]">{filter.icon({ className: 'h-[16px] w-[16px]' })}</div>
        <div className="text-cf-light-3">{filter.label}</div>
      </div>
      <div className="px-1.5">
        <span>{isLabel(filter.type, value)}</span>
      </div>
      <SelectedFilterDropdown
        selectedFilter={selectedFilter}
        valueLabel={valueLabel}
        filter={filter.type}
        value={value}
        displayLabel={isEmpty ? <span className="text-cf-light-3">any</span> : undefined}
      />
      {!isEmpty && !hideReset && (
        <div
          role="button"
          tabIndex={0}
          className="cursor-pointer border-none px-2 transition hover:bg-cf-gray-5"
          onClick={resetFilter}
        >
          <CloseIcon className="text-cf-light-2" />
        </div>
      )}
    </div>
  </motion.div>
);

export const ActiveFilters = <T,>({
  getFilter,
  resetFilter,
  allFilters,
  isLabel,
  valueLabel,
  selectedFilter,
  showDefaults = false, // if true, show empty filters as "any"
  hideReset = false, // if true, hide the reset (close) button on each filter
}: {
  getFilter: <K extends keyof T>(filter: K) => T[K];
  resetFilter: <K extends keyof T>(filter: K) => void;
  allFilters: Filters<keyof T>;
  isLabel: <K extends keyof T>(type: K, value: T[K]) => string;
  valueLabel: <K extends keyof T>(type: K, value: T[K]) => ReactNode;
  selectedFilter: (type: Filters<keyof T>[number]['type']) => ReactNode;
  showDefaults?: boolean;
  hideReset?: boolean;
}) => (
  <AnimatePresence>
    {allFilters.map((filter) => {
      const value = getFilter(filter.type);

      const isEmpty =
        !value ||
        (Array.isArray(value) && value.length === 0) ||
        (Array.isArray(value) && value[0] === 0 && value[1] === 0);

      if (isEmpty && !showDefaults) return null;

      return (
        <ActiveFilterLabel
          key={filter.type.toString()}
          filter={filter}
          value={value}
          resetFilter={() => resetFilter(filter.type)}
          isLabel={isLabel}
          valueLabel={valueLabel}
          selectedFilter={selectedFilter}
          isEmpty={isEmpty}
          hideReset={hideReset}
        />
      );
    })}
  </AnimatePresence>
);
